import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { apiGet } from '../client'
import { dealsKey } from './deals'
import { jobsKey } from './jobs'
import type { components } from '../generated/schema'
import type { JobStatus } from '../types'

type AnalysisJob = components['schemas']['AnalysisJobOut']

/**
 * Which statuses mean "finished" is a backend fact, derived from the generated
 * union rather than restated. The old frontend kept its own list and it drifted:
 * it still names two statuses this backend has never produced.
 */
const TERMINAL: ReadonlySet<JobStatus> = new Set<JobStatus>(['completed', 'failed'])

export function isTerminal(status: JobStatus): boolean {
  return TERMINAL.has(status)
}

export const analysisJobKey = (id: string) => ['analysis-job', id] as const

/**
 * Poll one job until it finishes.
 *
 * The query layer owns the polling, which the old 180-iteration for-loop did
 * not: this survives a tab switch, cancels on unmount, de-duplicates across
 * components, and stops the moment the job reaches a terminal state.
 */
export function useAnalysisJob(jobId: string | null) {
  const queryClient = useQueryClient()

  const query = useQuery<AnalysisJob>({
    queryKey: analysisJobKey(jobId ?? 'none'),
    queryFn: () => apiGet<AnalysisJob>(`/analysis/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (q) => (q.state.data && isTerminal(q.state.data.status) ? false : 2000),
    staleTime: 0,
  })

  const status = query.data?.status
  useEffect(() => {
    if (status && isTerminal(status)) {
      void queryClient.invalidateQueries({ queryKey: dealsKey })
      void queryClient.invalidateQueries({ queryKey: jobsKey })
    }
  }, [status, queryClient])

  return query
}
